import { useRouter } from 'next/router'
import { useContext, useEffect } from 'react'

import MyProfileContext from '@/components/Context/MyProfileContext'
import { FEATURE, isFeatureEnabled } from '@/lib/featureFlags'
import { EntityList } from '@/types/Entity'

/**
 * Feature flag des campagnes SMS/email patients (frontoffice pharmacie Care).
 *
 * Piloté par le système de feature flags GROUP (API `config/feature_flags.php`,
 * clé `care_campaigns`), exposé dans le profil (`dataMyProfile.featureFlags`) et
 * résolu côté API par environnement : actif en dev/stage, OFF en prod.
 *
 * L'entité est fixée à `PHARMEDICARE` (la clé sous laquelle l'API expose le flag),
 * volontairement PAS `dataMyProfile.entityActive` : le menu v2 affiche toutes les
 * routes accessibles quelle que soit l'entité courante, se caler sur `entityActive`
 * masquerait l'entrée dès que le pharmacien navigue hors Care.
 *
 * Périmètre : gating FRONT uniquement (entrée de menu + pages). Les endpoints
 * `care/campaigns` restent joignables — voir le commentaire dans la config API.
 */
export const isCareCampaignsEnabled = (featureFlags: any): boolean =>
  isFeatureEnabled(featureFlags, FEATURE.CARE_CAMPAIGNS, EntityList.PHARMEDICARE)

/**
 * Garde des pages `/frontoffice/care/campaigns` : renvoie vers la home quand le flag est
 * OFF, l'entrée de menu masquée n'empêchant pas l'accès par URL directe. Retourne l'état
 * du flag pour que la page n'affiche rien en attendant la redirection.
 *
 * Le prédicat pur `isCareCampaignsEnabled` sert aux routes (`fnDisplay`), évaluées hors
 * composant React avec le profil passé en argument.
 */
export const useCareCampaignsPageGuard = (): boolean => {
  const router = useRouter()
  const { dataMyProfile, loadingProfile } = useContext(MyProfileContext)
  const enabled = isCareCampaignsEnabled(dataMyProfile?.featureFlags)

  useEffect(() => {
    if (!loadingProfile && dataMyProfile && !enabled) {
      router.replace('/')
    }
  }, [loadingProfile, dataMyProfile, enabled, router])

  return enabled
}
